import { useFindBySurveyQuery } from 'client/modules/Surveys/savedSearchApiSlice';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const defaultAudience = {
  dateRange: ['now/y', 'now'],
  savedSearchId: 'all',
};

const AnalyzeAudienceContext = createContext(defaultAudience);

function readDateRangeFromParams(searchParams) {
  const since = searchParams.get('since');
  const until = searchParams.get('until');
  if (since || until) {
    return [since || '', until || ''];
  }
  return defaultAudience.dateRange;
}

export function AnalyzeAudienceProvider({ children, survey }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateRange = useMemo(() => readDateRangeFromParams(searchParams), [searchParams]);
  const savedSearchId = searchParams.get('savedSearch') || defaultAudience.savedSearchId;
  const { data: searches, isLoading } = useFindBySurveyQuery(survey?.id, { skip: !survey?.id });

  const setDateRange = useCallback(
    nextRange => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (nextRange?.length) {
            if (nextRange[0]) {
              next.set('since', nextRange[0]);
            } else {
              next.delete('since');
            }
            if (nextRange[1]) {
              next.set('until', nextRange[1]);
            } else {
              next.delete('until');
            }
          } else {
            next.delete('since');
            next.delete('until');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSavedSearchId = useCallback(
    id => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          const value = id || defaultAudience.savedSearchId;
          if (value === defaultAudience.savedSearchId) {
            next.delete('savedSearch');
          } else {
            next.set('savedSearch', value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const audienceParams = useMemo(
    () => ({
      since: dateRange?.[0] || undefined,
      until: dateRange?.[1] || undefined,
      saved_search_id: savedSearchId !== 'all' ? savedSearchId : undefined,
    }),
    [dateRange, savedSearchId],
  );

  return (
    <AnalyzeAudienceContext.Provider
      value={{
        survey,
        dateRange,
        setDateRange,
        savedSearchId,
        setSavedSearchId,
        searches,
        isLoadingSearches: isLoading,
        audienceParams,
      }}
    >
      {children}
    </AnalyzeAudienceContext.Provider>
  );
}

AnalyzeAudienceProvider.propTypes = {
  children: PropTypes.node.isRequired,
  survey: PropTypes.object.isRequired,
};

export const useAnalyzeAudience = () => useContext(AnalyzeAudienceContext);
